import React, {useState, useEffect, useImperativeHandle, forwardRef} from 'react';
import {Box, Flex, useTheme, Text} from '@chakra-ui/react';
import {motion, PanInfo, useMotionValue, useTransform, AnimatePresence} from 'framer-motion';
import {TextA14} from '../typography';
import {Event, Offer, Place, Post} from '../../types/article';
import styled from 'styled-components';
import Icon from '../Icon';

type SwipeItem = Event | Offer | Place | Post;

interface SwipeCardProps {
  item: SwipeItem;
  onSwipeLeft: (item: SwipeItem) => void;
  onSwipeRight: (item: SwipeItem) => void;
  renderCard: (item: SwipeItem) => React.ReactNode;
  itemType: 'event' | 'offer' | 'place' | 'post';
  index?: number;
  totalCards?: number;
}

export interface SwipeCardRef {
  swipeLeft: () => void;
  swipeRight: () => void;
}

const dragThreshold = 100;
const velocityThreshold = 400;

const CardContainer = styled(motion.div)<{$index?: number; $totalCards?: number}>`
  position: absolute;
  width: 100%;
  max-width: 350px;
  height: auto;
  border-radius: 12px;
  overflow: hidden;
  transform-origin: center center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  cursor: ${(props) => (props.$index === 0 ? 'grab' : 'default')};
  &:active {
    cursor: ${(props) => (props.$index === 0 ? 'grabbing' : 'default')};
  }
`;

const SwipeBadge = ({type, theme}) => (
  <Box
    position="absolute"
    top="40px"
    left="50%"
    transform={`translateX(-50%)`}
    padding="10px 20px"
    borderRadius="50px"
    backgroundColor={type === 'bookmark' ? 'rgba(39, 106, 205, 0.9)' : 'rgba(255, 90, 95, 0.9)'}
    border="2px solid white"
    boxShadow="0 8px 16px rgba(0, 0, 0, 0.2)"
    backdropFilter="blur(4px)"
    zIndex="10"
    minWidth="120px"
    textAlign="center">
    <Flex alignItems="center" justifyContent="center" gap={2}>
      <Icon
        iconName={type === 'bookmark' ? 'fi fi-rr-bookmark' : 'fi fi-rr-time-past'}
        style={{fontSize: '16px'}}
        color="white"
      />
      <TextA14 color="white" fontWeight="bold" letterSpacing="0.5px">
        {type === 'bookmark' ? 'Bookmark' : 'Not Now'}
      </TextA14>
    </Flex>
  </Box>
);

const SwipeCard = forwardRef<SwipeCardRef, SwipeCardProps>(
  ({item, onSwipeLeft, onSwipeRight, renderCard, itemType, index = 0, totalCards = 1}, ref) => {
    const theme = useTheme();
    const [exitX, setExitX] = useState<number>(0);
    const [cardHeight, setCardHeight] = useState<number>(0);
    const cardRef = React.useRef<HTMLDivElement>(null);

     const [isDragging, setIsDragging] = useState<boolean>(false);

    const x = useMotionValue(0);
    const y = useMotionValue(0);
    const rotate = useTransform(x, [-250, 0, 250], [-25, 0, 25]);

    const scale = useTransform(
      x,
      [-400, -300, -200, -100, 0, 100, 200, 300, 400],
      [1.12, 1.09, 1.06, 1.03, 1, 1.03, 1.06, 1.09, 1.12]
    );

    const verticalLift = useTransform(
      x,
      [-400, -300, -200, -100, 0, 100, 200, 300, 400],
      [0, 0, 0, 0, 0, 0, 0, 0, 0]
    );

    const xAbsolute = useTransform(x, (value) => Math.abs(value));
    const opacity = useTransform(xAbsolute, [0, 150], [1, 1]);

    const likeOpacity = useTransform(x, [0, 25, 100], [0, 1, 1]);
    const dislikeOpacity = useTransform(x, [-100, -25, 0], [1, 1, 0]);

    useImperativeHandle(ref, () => ({
      swipeLeft: () => {
        if (index === 0 && exitX === 0) {
          x.set(0);
          y.set(0);

          setTimeout(() => {
            setExitX(-(window.innerWidth * 1.5));
            onSwipeLeft(item);
          }, 10);
        }
      },
      swipeRight: () => {
        if (index === 0 && exitX === 0) {
          x.set(0);
          y.set(0);

          setTimeout(() => {
            setExitX(window.innerWidth * 1.5);
            onSwipeRight(item);
          }, 10);
        }
      },
    }));

    useEffect(() => {
      if (cardRef.current) {
        setCardHeight(cardRef.current.offsetHeight);
      }
    }, [item]);

    useEffect(() => {
      if (exitX !== 0) {
        y.set(0);
      }
    }, [exitX, y]);

    useEffect(() => {
      if (index === 0 && !isDragging && exitX === 0) {
        y.set(0);
      }
    }, [index, y, isDragging, exitX]);

    // Add this new effect to immediately reset Y when a card becomes the active card
    useEffect(() => {
      // This ensures that when a card transitions to being the active card (index 0),
      // its Y position is immediately reset to 0, regardless of the previous card's state
      if (index === 0) {
        y.set(0);
      }
    }, [index, y]);

    const handleDragStart = () => {
      if (index === 0) {
        setIsDragging(true);
      }
    };

    const handleDrag = (event, info: PanInfo) => {
      if (index === 0) {
        // Keep y position at 0 during drag
        y.set(0);
      }
    };

    const handleDragEnd = (info: PanInfo) => {
      if (index !== 0) return;

      setIsDragging(false);

      if (info.offset.x > dragThreshold || info.velocity.x > velocityThreshold) {
        y.set(0);
        setExitX(window.innerWidth * 1.5);
        onSwipeRight(item);
      } else if (info.offset.x < -dragThreshold || info.velocity.x < -velocityThreshold) {
        y.set(0);
        setExitX(-(window.innerWidth * 1.5));
        onSwipeLeft(item);
      } else {
        setExitX(0);
      }
    };

    const getStackStyles = () => {
      if (index === 0) return {};

      return {
        scale: 1 - index * 0.03,
        y: index * 10,
        opacity: 1,
        filter: 'none',
        zIndex: totalCards - index,
      };
    };

    const cardTransition = {
      type: 'spring',
      damping: 14,
      stiffness: 55,
      mass: 0.6,
      duration: 0.3,
      bounce: 0.35,
    };

    const backgroundToActiveTransition = {
      type: 'spring',
      damping: 16,
      stiffness: 50,
      mass: 0.7,
      duration: 0.3,
      bounce: 0.25,
    };

    return (
      <CardContainer
        ref={cardRef}
        $index={index}
        $totalCards={totalCards}
        style={{
          ...(index === 0
            ? {
                x,
                y,
                rotate,
                opacity,
                scale,
              }
            : {}),
          zIndex: totalCards - index,
        }}
        initial={index === 0 ? {scale: 1, opacity: 1} : {...getStackStyles()}}
        animate={
          index === 0
            ? exitX !== 0
              ? {scale: 1, opacity: 0.8, x: exitX}
              : {scale: 1, opacity: 1, x: 0}
            : {...getStackStyles()}
        }
        transition={index === 1 ? backgroundToActiveTransition : cardTransition}
        drag={index === 0 ? 'x' : false}
        dragConstraints={index === 0 ? {left: 0, right: 0} : undefined}
        dragElastic={0.4}
        onDragStart={handleDragStart}
        onDrag={handleDrag}
        onDragEnd={(_, info) => handleDragEnd(info)}
        layout
        layoutId={`card-${item.id}`}>
        <Box position="relative" width="100%" height="auto">
          {renderCard(item)}
        </Box>

        {index === 0 && (
          <>
            <motion.div
              style={{
                opacity: likeOpacity,
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 10,
              }}>
              <SwipeBadge type="bookmark" theme={theme} />
            </motion.div>

            <motion.div
              style={{
                opacity: dislikeOpacity,
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 10,
              }}>
              <SwipeBadge type="not-now" theme={theme} />
            </motion.div>
          </>
        )}
      </CardContainer>
    );
  }
);

export default SwipeCard;
